import { useState } from 'react';
import { User, Check } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useUpdateProfile } from '../../api/hooks/useUser';
import { Button } from '../actions/Button';
import { TextInput } from '../inputs/TextInput';
import { Alert } from '../feedback/Alert';
import { DeleteAccountModal } from '../modals/DeleteAccountModal';

export function ProfileTab() {
  const { user } = useAuth();
  const updateProfile = useUpdateProfile();

  const [name, setName] = useState(user?.name || '');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [avatarError, setAvatarError] = useState(false);

  const hasChanges = name !== user?.name;

  const handleSave = async () => {
    if (!hasChanges) return;

    try {
      await updateProfile.mutateAsync({ name });
      setSuccessMessage('Profile updated successfully');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      // Error handled by mutation
    }
  };

  return (
    <div className="max-w-lg space-y-8">
      {/* Avatar Section */}
      <div className="flex items-center gap-4">
        {user?.avatar_url && !avatarError ? (
          <img
            src={user.avatar_url}
            alt={user.name}
            className="w-20 h-20 rounded-full object-cover"
            referrerPolicy="no-referrer"
            onError={() => setAvatarError(true)}
          />
        ) : (
          <div className="w-20 h-20 rounded-full bg-primary flex items-center justify-center">
            <User className="w-10 h-10 text-text-inverse" />
          </div>
        )}
        <div>
          <p className="text-lg font-semibold text-text-primary">{user?.name}</p>
          <p className="text-sm text-text-secondary">{user?.email}</p>
        </div>
      </div>

      {/* Profile Form */}
      <div className="space-y-4">
        <TextInput
          label="Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Your name"
        />

        <div>
          <label className="block text-sm font-medium text-text-primary mb-2">
            Email
          </label>
          <div className="flex items-center gap-2 h-12 px-4 bg-background border border-border rounded-md text-text-secondary">
            <span>{user?.email}</span>
            {user?.email_verified_at && (
              <span className="ml-auto inline-flex items-center gap-1 text-xs text-success">
                <Check size={12} />
                Verified
              </span>
            )}
          </div>
          <p className="mt-1 text-xs text-text-secondary">
            Email cannot be changed
          </p>
        </div>

        {successMessage && (
          <Alert variant="success">{successMessage}</Alert>
        )}

        {updateProfile.error && (
          <Alert variant="error">Failed to update profile. Please try again.</Alert>
        )}

        <Button
          onClick={handleSave}
          disabled={!hasChanges || updateProfile.isPending}
          fullWidth
        >
          {updateProfile.isPending ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>

      {/* Delete Account */}
      <div className="pt-8 border-t border-border">
        <h3 className="text-sm font-semibold text-error mb-2">Delete Account</h3>
        <p className="text-sm text-text-secondary mb-4">
          Permanently delete your account and all associated data. This action cannot be undone.
        </p>
        <Button
          variant="secondary"
          onClick={() => setShowDeleteModal(true)}
          className="!border-error !text-error hover:!bg-error/10"
        >
          Delete Account
        </Button>
      </div>

      {/* Delete Account Modal */}
      <DeleteAccountModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
      />
    </div>
  );
}
